import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { connect } from "react-redux";
import { notify } from "reapop";
import {parseISO, isBefore, addMinutes, differenceInSeconds, formatISO} from "date-fns";
import useRedirectUrl from "@noku-tech/nokulibrary-frontend/dist/utils/hooks/useRedirectUrl";

import SplashLogo from "@noku-tech/nokulibrary-frontend/dist/Output/SplashLogo/SplashLogo";
import OnboardingStepList from "@noku-tech/nokulibrary-frontend/dist/Output/OnboardingStepList/OnboardingStepList";
import BaseInput from 'noku-library/dist/Input/BaseInput/BaseInput';

import Button from "../../components/UI/Input/Button/Button";
// import logobianco from "../../assets/img/logo_white.svg";
import classes from "./ValidateCodePage.module.scss";
import AxiosCaller from "../../Utils/AxiosCaller";
import redirectUri from "../../Utils/RedirectUrlGenerator.js";

const useLogoBlack = process.env.REACT_APP_USE_LOGO_BLACK === "true";

const ValidateCodePage = ({ notify = () => { }, currentUser, customizationVariables }) => {
  const [code, setCode] = useState("");
  const [validateDisabled, setValidateDisabled] = useState(true);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [remainingTimerSec, setRemainingTimerSec] = useState();
  const [needClick, setNeedClick] = useState(currentUser?.isNewUser ? false : true);
  const [sendEmailDisabled, setSendEmailDisabled] = useState(false);
  const history = useHistory();
  const timerRef = useRef(0);
  const { t } = useTranslation('noku-account');
	const { redirectUri: redirectURL, clientId, redirectHost } = useRedirectUrl();

  useEffect(() => {
    if(!currentUser?.id) {
      return history.push(redirectUri(`/`));
    }
    if(!currentUser?.isNewUser) {
      setNeedClick(true);
    }
  }, [currentUser, history]);

  useEffect(() => {
    const lastOTPRefresh = window.localStorage.getItem("lastOTPRefresh");
    const now = new Date();
    const lastRefresh = parseISO(lastOTPRefresh);

    if (lastOTPRefresh && isBefore(now, lastRefresh) ) {
      setRemainingTimerSec(differenceInSeconds(lastRefresh, now));
      startTimer();
    } else {
      setRemainingTimerSec(5 * 60);
    }
  }, []);

  const handleValidate = async () => {
    try {
      const { data: verifyResponse } = await AxiosCaller.post(`/api/noku-id/verify-email-code${redirectHost ? `?redirect_uri=${encodeURIComponent(redirectURL)}&client_id=${clientId}` : ""}`, { emailCode: code });

      if (!verifyResponse) {
        throw new Error("invalidCode");
      }
      if (verifyResponse?.redirectURI) {
        window.localStorage.setItem('codeCountdown', "");
        window.location.href = verifyResponse?.redirectURI;
      }
    } catch (error) {
      notify({
        title: 'Error',
        message: t(error?.message),
        status: 'error',
        dismissible: true,
        dismissAfter: 3000,
        closeButton: true
      });
    }
  };

  const handleRefreshOTP = async () => {
    if (refreshDisabled) {
      return;
    }
    setSendEmailDisabled(true);
    setRefreshDisabled(true);
    setTimeout(() => {
      setRefreshDisabled(false);
    }, 5000);

    const { data: otpResponse } = await AxiosCaller.get("/require-otp");
    if (otpResponse?.data?.sentEmail) {
      startTimer();
      window.localStorage.setItem("lastOTPRefresh", formatISO(addMinutes(new Date(), 5)));
      setTimeout(() => {
        setNeedClick(false);
      }, 1000);
    }
  };

  const startTimer = () => {
    if(timerRef.current) {
      return;
    }
    timerRef.current = setInterval(() => {
      setRemainingTimerSec((prev) => {
        return prev - 1;
      });
    }, 1000);
  };

  const handleCodeChange = (_, value) => {
    setCode(value);
    if (value) {
      setValidateDisabled(false);
    } else {
      setValidateDisabled(true);
    }
  };

  useEffect(() => {
    if (remainingTimerSec < 0) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      setRemainingTimerSec(5 * 60);
    }
  }, [remainingTimerSec]);

  const renderTimer = () => {
    return <div className={`${needClick ? 'flex-container' : ''} ${classes.sendNewCodeDescription}`}>{t("newMailIn", { remainingMinutes: `${parseInt(remainingTimerSec / 60)}:${String("0" + remainingTimerSec % 60).slice(-2)}` })}</div>;
  };

  const showTimer = () => {
    return remainingTimerSec > 1 && remainingTimerSec < 5 * 60;
  };

  const renderButtons = () => {
    if(needClick) {
      if(showTimer()) { 
        return renderTimer();
      } else {
        return <>
          <div className={`flex-container ${classes.sendEmailContainer}`}>
            <Button
              type="button"
              onClick={handleRefreshOTP}
              disabled={sendEmailDisabled}
            >
              {t("validateCodeDescription4")}
            </Button>
          </div>
        </>;
      }
    } else {
      return <>
        <BaseInput
          label="Code"
          labelClassName={classes.label}
          wrapperClassName={classes.inputWrapper}
          changed={handleCodeChange}
          value={code}
        />
        <Button
          type="button"
          onClick={handleValidate}
          disabled={validateDisabled}
          className={classes.btn}
        >
          {t("validate")}
        </Button>
        {
          showTimer() ? (
            renderTimer()
          ) : ( <div className={classes.footer}><div className={classes.noMailDescription} onClick={handleRefreshOTP}> {t("missingEmail")} {t("click")} <span className={`${classes.link} ${classes.uppercase}`}>{t("here")}</span> {t("toReceiveNewMail")}</div> </div> )
        }
      </>;
    }
  }; 

  return (
    <div className={`login ${classes.validateCodePage}`}>
      <div className="flex-container stretch">
        <div className={`block flex-container ${classes.noBackgroundColor}`}>
          <SplashLogo className={classes.splashLogo} useLogoBlack={useLogoBlack} />
          <OnboardingStepList className={classes.stepList} activeStep={2} />
          <div className="z9">
            <div className={classes.loginCard}>
              <div className={classes.title}>{t("verifyAccount")}</div>
              <div className={classes.descriptionRow}>{t(`validateCodeDescription${currentUser?.isNewUser ? '3' : '1'}`)} {needClick ? null : t("validateCodeDescription2")}</div>
              { renderButtons() }
            </div>
          </div>
          <div className={classes.backgroundImage} />
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    notify: notification =>
      dispatch((_, getState) => {
        notify(notification)(_, getState);
      })
  };
};

const mapStateToProps = ({ base }) => {
  return { 
    customizationVariables: base.customizationVariables,
    currentUser: base.currentUser
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ValidateCodePage);
